/**
 * PushPermissionPrompt
 *
 * One-time prompt asking authenticated users to enable native push notifications.
 * - Only renders inside the Capacitor native shell (auto-noops on web)
 * - Only shows if permission hasn't already been granted
 * - Remembers dismissal per-user in localStorage so we don't nag
 * - Shows on a short delay after login so we don't collide with other startup UI
 */
import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const STORAGE_KEY_PREFIX = 'push-prompt-dismissed:';
const SHOW_DELAY_MS = 2500;

export default function PushPermissionPrompt({ user }) {
  const [open, setOpen] = useState(false);
  const push = usePushNotifications();

  const userId = user?.userId || user?.user_id || user?.id || user?.email;
  const storageKey = userId ? `${STORAGE_KEY_PREFIX}${userId}` : null;

  useEffect(() => {
    // Guard rails: only prompt when we have all the pieces
    if (!user || !userId) return;
    if (!push.supported) return;                      // web -> skip
    if (push.permissionState === 'granted') return;   // already enabled
    if (push.permissionState === 'denied') return;    // user explicitly blocked -> don't nag
    if (!storageKey) return;
    if (localStorage.getItem(storageKey)) return;     // already dismissed once

    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user, userId, push.supported, push.permissionState, storageKey]);

  const handleEnable = async () => {
    const result = await push.registerForPush();
    if (result.ok || result.reason === 'permission_denied') {
      // Whether they granted or denied at the OS level, don't ask again on this device
      if (storageKey) localStorage.setItem(storageKey, String(Date.now()));
    }
    setOpen(false);
  };

  const handleDismiss = () => {
    if (storageKey) localStorage.setItem(storageKey, String(Date.now()));
    setOpen(false);
  };

  if (!push.supported) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleDismiss())}>
      <DialogContent className="sm:max-w-md" data-testid="push-permission-prompt">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-orange-100">
            <BellRing className="h-7 w-7 text-orange-600" />
          </div>
          <DialogTitle className="text-center">Stay on top of the field</DialogTitle>
          <DialogDescription className="text-center pt-2">
            Turn on push alerts to get notified the moment a field log is submitted,
            an incident is reported, or a JSA needs your review — even when the app is closed.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-col-reverse sm:space-x-0">
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full"
            data-testid="push-prompt-not-now-btn"
          >
            Not now
          </Button>
          <Button
            onClick={handleEnable}
            disabled={push.registering}
            className="w-full bg-orange-600 hover:bg-orange-700"
            data-testid="push-prompt-enable-btn"
          >
            <BellRing className="mr-2 h-4 w-4" />
            {push.registering ? 'Enabling…' : 'Enable Push Alerts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
